import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ExtractedEvent } from '../types';
import type { DateCorrection } from '../lib/useRecalculateDate';
import { eventDateEditError, eventInputForAllDay, isoToLocalInput, resolveEventDateEdit } from '../lib/datetime';
import { isAllDayEvent } from '../lib/eventDates';

interface Props {
  event: ExtractedEvent;
  /** True while a recalculate is in flight for this event. */
  recalcBusy?: boolean;
  /** True while a mark-wrong / correction is in flight for this event. */
  markBusy?: boolean;
  /** Re-run the AI extractor on this event's original post. */
  onRecalculate: (e: ExtractedEvent) => void;
  /** Flag the date wrong (no `correction`) or apply a corrected date. */
  onMarkWrong: (e: ExtractedEvent, correction?: DateCorrection) => void;
  /** Layout variant — controls the trigger's spacing classes to match the row/card. */
  variant?: 'compact' | 'card';
  /** Override the trigger's className entirely (e.g. to fit an action bar).
   *  When set, `variant` no longer affects the trigger styling. */
  triggerClassName?: string;
}

/**
 * Per-event admin calendar menu that replaces the old standalone "Recalculate
 * date" icon. The calendar-icon trigger opens a small popover with:
 *   1. Recalculate date — re-runs the AI extractor (async, ~30s).
 *   2. Mark wrong date/time — instant, no confirm. Badges the event and logs
 *      an AI-training signal without requiring a correction.
 *   3. Fix date/time — NY wall-clock/date inputs to supply corrected values.
 *      The server owns confirmation and explicit wrong-date flag preservation.
 *
 * Single source of truth so the four render sites (EventList compact/card,
 * HomePage compact/card) stay in sync — see repo Pitfall #13.
 */
export default function EventDateMenu({
  event,
  recalcBusy = false,
  markBusy = false,
  onRecalculate,
  onMarkWrong,
  variant = 'compact',
  triggerClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const [showFix, setShowFix] = useState(false);
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const flagged = event.dateFlaggedWrong === true;
  const busy = recalcBusy || markBusy;

  // The panel is rendered with position:fixed and manually positioned so it
  // escapes the event-list's `overflow: hidden` (which would otherwise clip it)
  // and flips away from the viewport edges instead of being cut off.
  const positionPanel = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const r = trigger.getBoundingClientRect();
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Horizontal: align the panel's left edge to the trigger; if that overflows
    // the right edge, right-align it to the trigger instead, then clamp.
    let left = r.left;
    if (left + pw > vw - margin) left = r.right - pw;
    left = Math.max(margin, Math.min(left, vw - pw - margin));
    // Vertical: open below by default; flip above when there's no room below.
    let top = r.bottom + 4;
    if (top + ph > vh - margin && r.top - ph - 4 >= margin) top = r.top - ph - 4;
    setPanelStyle({ position: 'fixed', top, left, visibility: 'visible' });
  }, []);

  // Close on outside click or Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)
        && panelRef.current && !panelRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position the fixed panel once it's in the DOM, and keep it anchored to the
  // trigger while the user scrolls or resizes (recomputed, not closed).
  useLayoutEffect(() => {
    if (!open) return;
    positionPanel();
    const onReflow = () => positionPanel();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, showFix, fixError, isAllDay, positionPanel]);

  // Seed the inline fixer from the current event whenever it's revealed.
  function openFixer() {
    const allDay = isAllDayEvent(event);
    setStartLocal(isoToLocalInput(event.startUtc, allDay));
    setEndLocal(isoToLocalInput(event.endUtc, allDay));
    setIsAllDay(allDay);
    setFixError(null);
    setShowFix(true);
  }

  function close() {
    setOpen(false);
    setShowFix(false);
    setFixError(null);
    setPanelStyle({ position: 'fixed', visibility: 'hidden' });
  }

  function handleRecalculate() {
    close();
    onRecalculate(event);
  }

  function handleMarkWrong() {
    close();
    onMarkWrong(event);
  }

  function handleSaveCorrection() {
    const temporal = resolveEventDateEdit({ startLocal, endLocal, isAllDay }, event);
    if (!temporal.ok) {
      setFixError(eventDateEditError(temporal));
      return;
    }
    close();
    onMarkWrong(event, temporal.value);
  }

  const triggerClass = triggerClassName
    ? triggerClassName
    : variant === 'card'
      ? 'btn btn-link p-0 ms-1 text-muted small align-baseline mh-date-menu__trigger'
      : 'btn btn-link p-0 text-muted mh-event-row__edit mh-date-menu__trigger';

  const triggerTitle = busy
    ? 'Working…'
    : flagged
      ? 'Date flagged wrong — recalculate or fix the date'
      : 'Date tools — recalculate or mark the date wrong';

  return (
    <div className="mh-date-menu" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={triggerClass}
        onClick={(ev) => {
          ev.stopPropagation();
          setOpen((o) => {
            const next = !o;
            if (next) setPanelStyle({ position: 'fixed', visibility: 'hidden' });
            else setShowFix(false);
            return next;
          });
        }}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Date options for ${event.title}`}
        title={triggerTitle}
      >
        <i
          className={`bi ${busy ? 'bi-arrow-repeat mh-spin' : flagged ? 'bi-calendar-x' : 'bi-calendar-event'}${flagged ? ' text-danger' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          id={menuId}
          ref={panelRef}
          className="mh-date-menu__panel"
          role="menu"
          style={panelStyle}
          onClick={(ev) => ev.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="mh-date-menu__item"
            onClick={(ev) => { ev.stopPropagation(); handleRecalculate(); }}
          >
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            <span>Recalculate date</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="mh-date-menu__item mh-date-menu__item--danger"
            onClick={(ev) => { ev.stopPropagation(); handleMarkWrong(); }}
          >
            <i className="bi bi-calendar-x" aria-hidden="true" />
            <span>Mark wrong date/time</span>
          </button>
          <div className="mh-date-menu__divider" role="separator" />
          {!showFix ? (
            <button
              type="button"
              role="menuitem"
              className="mh-date-menu__item"
              onClick={(ev) => { ev.stopPropagation(); openFixer(); }}
            >
              <i className="bi bi-calendar-check" aria-hidden="true" />
              <span>Fix date/time…</span>
            </button>
          ) : (
            <div className="mh-date-menu__fix" onClick={(ev) => ev.stopPropagation()}>
              <label className="mh-date-menu__field">
                <span>{isAllDay ? 'Start date' : 'Start'}</span>
                <input
                  type={isAllDay ? 'date' : 'datetime-local'}
                  step={isAllDay ? undefined : 'any'}
                  className="form-control form-control-sm"
                  value={startLocal}
                  onChange={(ev) => setStartLocal(ev.target.value)}
                />
              </label>
              <label className="mh-date-menu__field">
                <span>{isAllDay ? 'End date' : 'End'}</span>
                <input
                  type={isAllDay ? 'date' : 'datetime-local'}
                  step={isAllDay ? undefined : 'any'}
                  className="form-control form-control-sm"
                  value={endLocal}
                  onChange={(ev) => setEndLocal(ev.target.value)}
                />
              </label>
              <label className="mh-date-menu__check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isAllDay}
                  onChange={(ev) => {
                    const allDay = ev.target.checked;
                    setIsAllDay(allDay);
                    setStartLocal((local) => eventInputForAllDay(local, allDay));
                    setEndLocal((local) => eventInputForAllDay(local, allDay));
                    setFixError(null);
                  }}
                />
                <span>All day</span>
              </label>
              {fixError && <p className="small text-danger mb-2" role="alert">{fixError}</p>}
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                onClick={(ev) => { ev.stopPropagation(); handleSaveCorrection(); }}
                disabled={!startLocal}
              >
                Save corrected date
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
